// noinspection DuplicatedCode

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faMagnifyingGlassPlus,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import {
  ActionButtons,
  BLOCK_EDITOR_DEFAULTS_PROPS_VALUE,
  BLOCK_EDITOR_PROPTYPES,
  ContentBlockEditor,
} from 'components/ContentBlockEditor';
import PartnerProfileService from 'api/PartnerProfileService';
import { Input } from 'components/Forms';
import './ItemBlockEditor.scss';
import Alert from 'components/Alert/Alert';
import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import ItemBlockItemInformation from './ItemBlockItemInformation';

const propTypes = BLOCK_EDITOR_PROPTYPES;
const defaultProps = BLOCK_EDITOR_DEFAULTS_PROPS_VALUE;

export default function ItemBlockEditor(props) {
  const { contentBlock, onCancel, partnerId, onSaveSuccessfully } = props;
  const [isShowItemEditor, setIsShowItemEditor] = useState(false);
  const [isSilentSaving, setIsSilentSaving] = useState(false);
  const [processTypeLabel, setProcessTypeLabel] = useState('Add');
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemInformationList, setItemInformationList] = useState([]);
  const [open, setOpen] = useState(false);
  const [itemParam, setItemParam] = useState('');
  const { handleSubmit, control, setValue, getValues } = useForm({
    defaultValues: contentBlock,
  });
  const mutationSaveBlock = useMutation(
    (block) => PartnerProfileService.addBlock(block),
    {
      onSuccess: ({ data }) => {
        setTimeout(() => {
          onCancel();
          onSaveSuccessfully(data);
        }, 1000);
      },
    }
  );
  const mutationUpdateBlock = useMutation(
    (block) => PartnerProfileService.updateBlock(block),
    {
      onSuccess: ({ data }) => {
        setTimeout(() => {
          onCancel();
          onSaveSuccessfully(data);
        }, 1000);
      },
    }
  );
  const mutationGetItems = useMutation(
    (blockId) => PartnerProfileService.getContentBlock(blockId),
    {
      onSuccess: ({ data }) => {
        if (data) {
          setValue('id', data?.id);
          setValue('description', data?.description);
          setValue('displayOrder', data?.displayOrder);
          setValue('partnerId', data?.partnerId);
          setValue('title', data?.title);
          setValue('contentType', data?.type);
          const mappedData = data?.data.map((x) => ({
            id: x.id,
            dateKey: x.dateKey,
            displayOrder: x.value?.displayOrder,
            title: x.value?.title,
            description: x.value?.description,
            mediaPath: x.value?.mediaPath,
            mediaPreviewPath: x.value?.mediaPreviewPath,
            mediaType: x.value?.mediaType,
          }));
          setItemInformationList(mappedData);
        }
      },
    }
  );
  const mutationDeleteBlockData = useMutation(
    (blockDataId) => PartnerProfileService.deleteBlockData(blockDataId),
    {
      onSuccess: () => {
        onSaveSuccessfully();
      },
    }
  );
  const isProcessing = () =>
    mutationSaveBlock.isLoading ||
    mutationSaveBlock.isSuccess ||
    mutationUpdateBlock.isLoading ||
    mutationUpdateBlock.isSuccess;
  const onSubmit = (formData) => {
    const block = {
      id: formData.id,
      partnerId: formData.partnerId,
      contentType: 'item_list',
      title: formData.title,
      description: formData.description,
      displayOrder: 1,
    };
    if (formData.id.toString().length > 0) {
      mutationUpdateBlock.mutate(block);
    } else {
      mutationSaveBlock.mutate(block);
    }
  };
  const showItemInformationEditor = () => {
    setIsShowItemEditor(true);
    setSelectedItem({
      data: [
        {
          dataKey: 'item_guid',
          value: null,
          groupKey: 'items',
        },
      ],
    });
  };
  const editItemInformation = (item) => {
    setIsShowItemEditor(true);
    setSelectedItem({
      data: [
        {
          dataKey: 'item_guid',
          value: item,
          groupKey: 'items',
        },
      ],
    });
  };
  const onBlockItemInformationSubmit = (item) => {
    if (item.data && item.data.length > 0) {
      const temp = item.data[0].value;
      if (itemInformationList.length === 0) {
        temp.displayOrder = temp.displayOrder > 0 ? temp.displayOrder : 1;
      } else {
        const orderNumbers = itemInformationList.map((f) => f.displayOrder);
        temp.displayOrder =
          temp.displayOrder > 0
            ? temp.displayOrder
            : Math.max(...orderNumbers) + 1;
      }
      temp.displayOrder = Number(temp.displayOrder);
      if (itemInformationList.length === 0 && getValues().id.length === 0) {
        setIsSilentSaving(true);
        setValue('contentType', 'item_list');
        PartnerProfileService.addBlock(getValues()).then(({ data }) => {
          if (data) {
            setValue('id', data?.id);
            setIsSilentSaving(false);
            onSaveSuccessfully(data);
            insertItemInformation(data?.id, temp, item.file, item.previewImage);
            setTimeout(() => setIsSilentSaving(false), 500);
          }
        });
        return;
      }
      if (temp.id.length === 0) {
        insertItemInformation(
          getValues().id,
          temp,
          item.file,
          item.previewImage
        );
      } else {
        updateItemInformation(temp.id, temp, item.file, item.previewImage);
      }
    }
  };
  const onBlockItemInformationCancel = () => {
    setIsShowItemEditor(false);
  };
  const mediaTypeText = (type) => {
    if (type === 'image') {
      return 'Image';
    }
    if (type === 'link') {
      return 'Link';
    }
    if (type === 'youtube') {
      return 'YouTube';
    }
    if (type === 'file') {
      return 'File';
    }
    return '';
  };
  const deleteItemInformation = (id) => {
    setOpen(true);
    setItemParam(id);
  };

  const onDeleteConfirm = () => {
    const temp = itemInformationList.filter((i) => i.id !== itemParam);
    setItemInformationList(temp);
    mutationDeleteBlockData.mutate(itemParam);
    // console.log(itemParam);
    setOpen(false);
  };

  const completePromise = () => {
    mutationGetItems.reset();
    mutationGetItems.mutate(getValues().id);
    onSaveSuccessfully();
    onBlockItemInformationCancel();
  };
  const savePreviewImage = (dataKey, files) =>
    PartnerProfileService.uploadFile({
      blockDataItemDataKey: dataKey,
      file: files,
      type: 'file_preview_upload',
    });
  const insertItemInformation = (
    blockInstanceId,
    value,
    files,
    previewImageFiles
  ) => {
    console.log('Inserting item information');
    PartnerProfileService.addBlockData(blockInstanceId, {
      dataKey: 'item_guid',
      value,
      groupKey: 'items',
    }).then(({ data }) => {
      if (data) {
        const finalPromise = () => {
          if (value.mediaType === 'image' || value.mediaType === 'file') {
            PartnerProfileService.uploadFile({
              blockDataItemDataKey: data.dataKey,
              file: files,
              type: 'file_upload',
            })
              .then(() => {
                completePromise();
              })
              .catch(() => {
                completePromise();
              });
          } else {
            completePromise();
          }
        };
        savePreviewImage(data.dataKey, previewImageFiles).then(() => {
          finalPromise();
        });
      }
    });
  };
  const updateItemInformation = (id, value, files, previewImageFiles) => {
    console.log('Updating item information');
    PartnerProfileService.updateBlockData(id, JSON.stringify(value)).then(
      ({ data }) => {
        if (data) {
          const finalPromise = () => {
            if (
              (value.mediaType === 'image' || value.mediaType === 'file') &&
              files &&
              files.length > 0
            ) {
              PartnerProfileService.uploadFile({
                blockDataItemDataKey: data.dataKey,
                file: files,
                type: 'file_upload',
              })
                .then(() => {
                  completePromise();
                })
                .catch(() => {
                  completePromise();
                });
            } else {
              completePromise();
            }
          };
          if (previewImageFiles && previewImageFiles.length > 0) {
            console.log('has preview');
            savePreviewImage(data.dataKey, previewImageFiles)
              .then(() => {
                finalPromise();
              })
              .catch(() => {
                finalPromise();
              });
          } else {
            finalPromise();
          }
        }
      }
    );
  };

  useEffect(() => {
    if (contentBlock) {
      setValue('id', contentBlock?.id);
      setValue('title', contentBlock?.title);
      if (contentBlock?.data && contentBlock?.data.length > 0) {
        setValue('content', contentBlock?.data[0]?.value);
      }
      setValue('description', contentBlock?.description);
      setProcessTypeLabel(
        contentBlock.id.toString().length > 0 ? 'Edit' : 'Add'
      );
      if (getValues().id.length > 0) {
        mutationGetItems.mutate(getValues().id);
      }
    }
    setValue('partnerId', partnerId);
  }, [partnerId, contentBlock]);

  return (
    <ContentBlockEditor title={`${processTypeLabel} Item List Block`}>
      {mutationSaveBlock.isError || mutationUpdateBlock.error ? (
        <Alert variant="danger" data-testid="Alert-error">
          {mutationSaveBlock.isError
            ? mutationSaveBlock.error.message
            : mutationUpdateBlock.error.message}
          .
        </Alert>
      ) : null}
      {mutationSaveBlock.isSuccess && (
        <Alert variant="success" data-testid="Alert-addSuccess">
          Item List Block is now added.
        </Alert>
      )}
      {mutationUpdateBlock.isSuccess && (
        <Alert variant="success" data-testid="Alert-updateSuccess">
          Item List Block has been updated.
        </Alert>
      )}
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="d-block mb-2">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="title" className="d-block mb-2">
            <strong>Block Title</strong>
          </label>
          <div className="d-flex space-between  align-items-center align-content-center">
            <div className="d-block flex-grow-1">
              <Input
                control={control}
                name="title"
                placeholder="Enter the block title"
                disabled={isProcessing()}
              />
            </div>
            <div
              className="d-flex align-items-center align-content-center ps-2 ps-xl-2 ps-lg-2 ps-md-2 ps-sm-0"
              style={{ paddingBottom: '10px' }}
            >
              <ActionButtons
                reverse
                isProcessing={isProcessing() || isSilentSaving}
                onCancel={onCancel}
                isUpdate={
                  contentBlock.id.toString().length > 0 ||
                  getValues().id.toString().length > 0
                }
              />
            </div>
          </div>
        </div>
        <div className="d-block mb-2">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="description" className="d-block mb-2">
            <strong>
              Block Description <i className="text-muted">(Optional)</i>
            </strong>
          </label>
          <Input
            name="description"
            control={control}
            placeholder="Enter the block description"
            disabled={isProcessing()}
          />
        </div>
        <div className="d-block mb-3">
          <div className="d-flex mb-3 align-content-center align-items-center">
            <div className="d-block flex-grow-1">
              <h4 className="pe-2 mb-0">Items</h4>
            </div>
            <div className="d-block">
              <Button
                type="button"
                className="mt-2 btn btn-primary btn-sm btn-pmi"
                onClick={showItemInformationEditor}
              >
                Add Item
              </Button>
            </div>
          </div>
          <div className="d-block table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Item Title</th>
                  <th>Description</th>
                  <th>Media</th>
                  <th style={{ width: '95px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mutationGetItems.isLoading && (
                  <tr>
                    <td colSpan="5">
                      <div className="text-muted text-center m-0">
                        <Spinner size="sm" animation="border" /> Loading...
                      </div>
                    </td>
                  </tr>
                )}
                {itemInformationList.length === 0 &&
                  !mutationGetItems.isLoading && (
                    <tr>
                      <td colSpan="5">
                        <p
                          className="text-muted p-0 m-0 text-center"
                          data-testid="Text-noItem"
                        >
                          No item is added.
                        </p>
                      </td>
                    </tr>
                  )}
                {itemInformationList.length > 0 &&
                  itemInformationList.map((item, index) => (
                    <tr key={`item-${index.toString()}`}>
                      <td>{item.displayOrder}</td>
                      <td>
                        {item.title.trim() !== '' ? item.title : '(no title)'}
                      </td>
                      <td>{item.description}</td>
                      <td>{mediaTypeText(item.mediaType)}</td>
                      <td>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-1"
                          onClick={() => editItemInformation(item)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-1"
                          onClick={() => deleteItemInformation(item.id)}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                        <a
                          href={item.mediaPath}
                          target="_blank"
                          className="btn btn-sm btn-link p-1"
                          rel="noreferrer"
                        >
                          <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
                        </a>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        </div>
        <div className="mb-0 text-end" />
      </Form>
      {isShowItemEditor && selectedItem && (
        <div className="item-editor-overlay">
          <ItemBlockItemInformation
            partnerId={partnerId}
            onCancel={onBlockItemInformationCancel}
            onSubmit={onBlockItemInformationSubmit}
            item={selectedItem}
          />
        </div>
      )}
      <ModalDialog
        open={open}
        setOpen={setOpen}
        header="Delete Item"
        message="You are about to delete a item. Are you sure about it?"
        onConfirm={onDeleteConfirm}
      />
    </ContentBlockEditor>
  );
}
ItemBlockEditor.propTypes = propTypes;
ItemBlockEditor.defaultProps = defaultProps;
